/** @jsxImportSource @emotion/react */

import { FC, useState } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import { generate as generateUniqueName } from 'randomstring'
import { ReactSortable } from 'react-sortablejs'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormHelperText,
  IconButton,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { FaqTypeEnum } from '__generatedTypes__/globalTypes'
import { SORTABLE_LIST_ANIMATION } from 'constants/consts'
import { EditorField } from 'components/Editor'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg'
import { ConfirmationModal } from 'components/Modals/ConfirmationModal/ConfirmationModal'
import { validateRequired, validateRequiredHtml } from 'utils/validation'
import { FaqListItem, FaqTypeOption } from './types'
import { getFaqTypeOption, getFaqTypeSelectItems } from './consts'
import { FAQ_HIGHER_SCORE_TYPE_ERROR } from './validation'
import * as classes from './styles'

type Props = {
  fields: FieldArrayRenderProps<FaqListItem, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<FaqListItem, HTMLElement>['meta']
  allowedTypes: FaqTypeEnum[]
  onDelete: (id: string) => void
}

const DND_ICON_CLASSNAME = 'DnDIcon_drag_handler'

export const FAQFieldArray: FC<Props> = ({ fields, meta, allowedTypes, onDelete }) => {
  const [indexToRemove, setIndexToRemove] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [draggingField, setDraggingField] = useState<string | null>(null)
  const showTypeSelect = allowedTypes.length >= 2
  const typeSelectItems = getFaqTypeSelectItems(allowedTypes)
  const defaultType = getFaqTypeOption(FaqTypeEnum.Standard)
  const fieldArray = (fields.value ?? []).map(({ id }) => ({ id }))
  const arrayError =
    meta.touched && Array.isArray(meta.error)
      ? meta.error.find((error) => error === FAQ_HIGHER_SCORE_TYPE_ERROR)
      : undefined

  // TODO: move to separate hook
  const handleSort = (newFieldArray: Array<{ id: string; chosen?: boolean }>) => {
    const chosenIndex = newFieldArray.findIndex(({ chosen }) => chosen !== undefined)

    if (chosenIndex !== -1) {
      const chosenId = newFieldArray[chosenIndex].id
      const previousIndex = fieldArray.findIndex(({ id }) => id === chosenId)

      if (previousIndex !== -1 && previousIndex !== chosenIndex) {
        fields.move(previousIndex, chosenIndex)
      }
    }
  }

  const handleAdd = () => {
    const id = generateUniqueName()
    fields.push({
      id,
      question: '',
      answerAsHtml: '',
      ...(showTypeSelect && { type: defaultType }),
    })
    setExpanded(id)
  }

  const handleConfirm = () => {
    if (indexToRemove !== null) {
      fields.remove(indexToRemove)
      setIndexToRemove(null)

      const entityId = fields.value[indexToRemove]?.entityId

      if (entityId) {
        onDelete(entityId)
      }
    }
  }

  return (
    <>
      <Typography variant="body2" css={classes.label}>
        List of all FAQ
      </Typography>
      <ReactSortable
        list={fieldArray}
        handle={`.${DND_ICON_CLASSNAME}`}
        setList={handleSort}
        animation={SORTABLE_LIST_ANIMATION}
        onStart={(props) => {
          const fieldId = props.item.id
          const isExpanded = expanded === fieldId
          if (isExpanded) {
            setDraggingField(fieldId)
          }
        }}
        onEnd={() => {
          setDraggingField(null)
        }}
      >
        {fields.map((field, index) => {
          const fieldId = fieldArray[index].id

          const isExpanded = expanded === fieldId
          const hasHigherScoreTypeError = meta.touched && Array.isArray(meta.error) && Boolean(meta.error[index])

          return (
            <Accordion
              key={field}
              id={fieldId}
              expanded={isExpanded}
              css={[classes.accordion, hasHigherScoreTypeError && classes.accordionError]}
            >
              <AccordionSummary css={[classes.accordionSummary]}>
                <Typography variant="body2" css={classes.label}>
                  {index + 1}.
                </Typography>
                <InputField
                  name={`${field}.question`}
                  placeholder="Enter Question"
                  validate={validateRequired}
                  css={classes.questionInput}
                />
                {showTypeSelect && (
                  <SelectField<FaqTypeOption>
                    name={`${field}.type`}
                    items={typeSelectItems}
                    placeholder="Type"
                    isNotNullable
                    className={classes.FAQ_TYPE_SELECT_CLASSNAME}
                  />
                )}
                <IconButton onClick={() => setExpanded(isExpanded ? null : fieldId)} data-testid="expand-button">
                  <ChevronDownIcon css={[classes.iconChevron, isExpanded && classes.iconChevronOpen]} />
                </IconButton>
                <IconButton onClick={() => setIndexToRemove(index)} data-testid="delete-faq">
                  <TrashIcon />
                </IconButton>
                <DnDIcon className={DND_ICON_CLASSNAME} data-testid="dnd-icon" />
              </AccordionSummary>
              <AccordionDetails css={classes.accordionDetails}>
                <div css={classes.editor}>
                  <EditorField
                    type="default"
                    name={`${field}.answerAsHtml`}
                    placeholder="Enter Answer"
                    validate={validateRequiredHtml}
                    disabled={draggingField === fieldId}
                  />
                </div>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </ReactSortable>
      {Boolean(meta.touched && arrayError) && <FormHelperText error>{arrayError}</FormHelperText>}
      <Button variant="outlined" color="blue" size="large" onClick={handleAdd} css={classes.addContentButton}>
        Add Content <AddRoundedIcon />
      </Button>
      <ConfirmationModal
        open={indexToRemove !== null}
        title={`Deleting FAQ item #${(indexToRemove ?? 0) + 1}`}
        subtitle={`Are you sure you want to delete this FAQ item?`}
        onClose={() => setIndexToRemove(null)}
        onConfirm={handleConfirm}
        confirmText="Delete"
        closeText="Cancel"
      />
    </>
  )
}
